import {_, React, observer, observable, computed, IDisposer, action, useEffect, classNames, useAsObservableSource, runInAction, autorun} from '@api'
import {InputBaseComponentProps} from '@material-ui/core/InputBase/InputBase'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {StoresInstance} from '@stores'
import {PrimaryIconButton, SearchField} from 'centric-ui-components'
import * as css from './SearchField2.css'
import {CircularProgress, Input} from '@material-ui/core'
import {Close, Search} from '@material-ui/icons'

enum KeyCode {
  ENTER = 13,
  ESC = 27
}

/// region Store

export class SearchField2Store extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<SearchField2Props, 'className'>) {
    super(stores)

    this.search = {active: this.props?.active, value: '', focus: false}
  }

  @observable search: { active: boolean, value: string, focus: boolean }

  input: HTMLInputElement

  @action onCancelSearch = (e) => {
    const {search, props, catalog} = this
    search.active = this.props.active || false
    catalog.searchActive = false
    search.value = ''
    props.onSearch(e, search.value)
  }

  @computed get hasText() {
    return this.search.value && this.search.value != ''
  }

  @action updateValue = (value) => {
    if (value != undefined && value != this.search.value) {
      runInAction(`Update search value`, () => {
        this.search.active = this.props.active || (value && value != '')
        this.search.value = value as string
      })
    }
  }

  @action cancel = () => {
    this.search.active = false
    this.search.value = ''
  }

  get active() {
    return this.props.active
  }

  @action onClick = (e) => {
    const {search, active, catalog, props: {onSearch}, input} = this
    if (search.active) {
      runInAction(() => {
        //search.value = ''
        search.active = active || false
        search.focus = false
        catalog.searchActive = false
      })
    } else {
      runInAction(() => {
        search.active = true
        catalog.searchActive = true
        input.focus()
      })
    }
    onSearch(e, search.value)
  }

  @action onChange = (e) => {
    const {search, props: {onSearch}} = this
    runInAction(() => {
      search.value = e.target.value
      search.focus = true
    })
    onSearch(e, search.value)
  }

  @action onKeyDown = (e) => {
    const {search, props: {onSearch}, active, onCancelSearch} = this
    if (e.which == KeyCode.ESC) {
      this.search = {active: active || search.value != '', value: '', focus: false}  // First clear the textbox, then close it if they hit escape when it's empty
      if (e.target.value === '') onCancelSearch(e)
      onSearch(e, this.search.value)
    } else if (e.which == KeyCode.ENTER) {
      onSearch(e, search.value)
    }
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {props} = this
        this.updateValue(props.value)
      })
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }
}

/// endregion

/// region Components

interface SearchField2Props {
  onSearch: (e, value: string) => void,
  className?: string
  smallSearchInput?: boolean
  autoFocus?: boolean
  value?: string
  style?: React.CSSProperties
  type?: string
  loading?: boolean
  animate?: boolean
  hideSearchIcon?: boolean
  active?: boolean
  placeholder?: string
  ref?: any
  inputProps?: InputBaseComponentProps;
}

export const SearchField2 = observer<SearchField2Props, HTMLDivElement>(function SearchField2({className, ...props}, ref) {
  const obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new SearchField2Store(stores, obsProps)),
    {onCancelSearch, search, hasText, onClick, onChange, onKeyDown, props: {autoFocus, active, loading, style, onSearch, smallSearchInput, hideSearchIcon, animate, ...rest}} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <div className={classNames(className, {[css.searchActive]: (search.active || hideSearchIcon)}, css.root)}
         ref={ref}
         data-loading={loading}
         data-type='SearchField2'
         data-hide-icon={hideSearchIcon}
         data-active={active || search.active}
         data-animate={animate}
         data-has-text={hasText === true}
         style={{flexGrow: smallSearchInput && (search.active || hideSearchIcon) ? 1 : undefined, ...style}}>

      {!hideSearchIcon && <PrimaryIconButton icon={<Search/>}
	                                         className={classNames(css.searchIcon)}
	                                         onClick={onClick}/>}
      <Input className={classNames({[css.noUnderline]: (hideSearchIcon && _.isEmpty(search.value)), [css.smallSearchInput]: smallSearchInput}, css.searchInput)}
             {...rest}
             autoFocus={autoFocus}
             inputRef={r => {store.input = r}}
             value={search.value}
             onChange={onChange}
             onKeyDown={onKeyDown}
      />
      {search.active && <CircularProgress size={18} className={classNames(css.loading, {[css.loader]: !smallSearchInput})}/>}
      {((hideSearchIcon ? !_.isEmpty(search.value) : true)) && <PrimaryIconButton icon={<Close/>}
	                                                                              data-icon='close'
	                                                                              wrapperClassName={classNames(css.closeIcon)}
	                                                                              onClick={onCancelSearch}/>
      }
    </div>
  )
}, {forwardRef: true})

SearchField2['defaultProps'] = {
  onSearch: () => { },
  placeholder: 'Search',
  animate: false
}

/// endregion




// WEBPACK FOOTER //
// ./src/shared/components/ui/components/SearchField2.tsx